import { useSyncExternalStore } from 'react';

/**
 * Camadas abertas por cima da tela (cartões e painéis do palco, folhas novas):
 * um registro só, na ordem em que abriram. O botão voltar do aparelho, o gesto
 * de voltar e o próprio palco perguntam aqui o que está aberto, em vez de cada
 * um manter a sua lista de seletores.
 */
export type LayerKind = 'card' | 'panel' | 'sheet';

type Entry = { id: string; kind: LayerKind; close: () => void };

const stack: Entry[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/** Registra uma camada aberta; devolve a função que a tira do registro. */
export function registerLayer(entry: Entry): () => void {
  const index = stack.findIndex((item) => item.id === entry.id);
  if (index >= 0) stack.splice(index, 1);
  stack.push(entry);
  emit();
  return () => {
    const at = stack.indexOf(entry);
    if (at >= 0) {
      stack.splice(at, 1);
      emit();
    }
  };
}

/** Fecha a camada de cima. Devolve falso quando não havia nenhuma. */
export function closeTopLayer(): boolean {
  const top = stack[stack.length - 1];
  if (!top) return false;
  top.close();
  return true;
}

/** Fecha tudo o que está por cima (painéis, cartões): a próxima tela abre limpa. */
export function closeAllLayers(): void {
  for (let guard = stack.length; guard > 0 && stack.length > 0; guard -= 1) closeTopLayer();
}

export function anyLayerOpen(): boolean {
  return stack.length > 0;
}

/** Tipos das camadas abertas, da mais antiga para a mais nova (para testes e diagnóstico). */
export function openLayerKinds(): LayerKind[] {
  return stack.map((item) => item.kind);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAnyLayerOpen(): boolean {
  return useSyncExternalStore(subscribe, anyLayerOpen, () => false);
}

/**
 * Folhas antigas, que ainda fecham com um toque no próprio fundo. Ficam aqui
 * até cada uma migrar para o registro acima.
 */
export const LEGACY_BACKDROPS = [
  '.cv-sheetwrap',
  '.ct-sheetwrap',
  '.ag-sheetwrap',
  '.editsheet-backdrop',
  '.am-backdrop',
  '.pw-back',
  '.aic-back',
  '.lgs-back',
  '.cl-back',
  '.spv-back',
  '.rk-backdrop',
  '.mb-light',
].join(', ');

/** Fecha a folha antiga que estiver aberta. Devolve falso quando não havia nenhuma. */
export function closeLegacySheet(): boolean {
  const sheets = document.querySelectorAll<HTMLElement>(LEGACY_BACKDROPS);
  const sheet = sheets[sheets.length - 1];
  if (!sheet) return false;
  sheet.click();
  return true;
}
